<template>
  <div class="chat-component">
    <div class="chat-wrap">
      <div class="chat-box">
        <!-- Status Thread -->
        <div class="status" v-if="!isNewTicket">
          <div class="close-chat" @click="$emit('closeChat')">
            <span class="mdi mdi-chevron-left"> </span>
          </div>
          <div class="people-tags">
            <div class="people-in-thread" :title="usersInChat">
              {{ usersInChat }}
            </div>
            <div class="tags">
              <small
                class="tag"
                v-for="tag in chat.meta.tags"
                :class="tag.type"
                :key="tag.type"
              >
                {{ tag.label }}
              </small>
            </div>
          </div>
          <div class="ml-3 loading" v-if="loading">
            <loading />
          </div>
          <div class="ml-3 action-wrapper" v-if="!loading">
            <i
              id="mark-as-not-read"
              @click="markAsNotRead"
              class="material-icons text-primary action-btn mr-1"
            >
              visibility_off
            </i>
            <b-tooltip target="mark-as-not-read">
              <div class="tooltip-info">
                {{ labels.btnMarkAsNotRead }}
              </div>
            </b-tooltip>
            <div
              v-if="
                !chat.meta.isMyTicket &&
                  !chat.meta.closed &&
                  canSeeOtherFeedbacks
              "
            >
              <b-button
                variant="success"
                class="complete-feedback-btn mt-1"
                :title="labels.btnCompleteFeedback"
                @click="completeFeedback"
              >
                <i class="material-icons">check_circle</i>
                <span class="ml-2 d-none d-sm-block">
                  {{ labels.btnCompleteFeedback }}
                </span>
              </b-button>
            </div>
          </div>
        </div>
        <!-- END - Status Thread -->

        <!-- Thread Messages -->
        <ChatWindow
          :thread="thread"
          :question="chat.meta.surveyQuestion || ''"
          :isNewTicket="isNewTicket"
          :isMyTicket="chat.meta.isMyTicket"
          @feeling="feeling = $event"
          @rateMessage="emitRateMessage"
        />
        <!-- End - Thread Messages -->

        <!-- Input Control -->
        <div class="text-box">
          <ChatQuickAnswer
            v-if="
              !isNewTicket &&
                !chat.meta.isMyTicket &&
                (chat.meta.isPositive === true ||
                  chat.meta.isPositive === undefined) &&
                chat.messages.length > 0 &&
                chat.messages[chat.messages.length - 1].owner === true &&
                chat.messages[chat.messages.length - 1].closed !== true
            "
            @sendMessage="sendMessage"
            class="quick-answer"
          />
          <ChatInputControl
            ref="inputControl"
            :isClosed="chat.meta.closed"
            :anonymousOption="showAnonymous"
            @sendMessage="sendMessage"
          />
        </div>
        <!-- END - Input Control -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ChatWindow from './ChatWindow.vue'
import ChatInputControl from './ChatInputControl.vue'
import ChatQuickAnswer from './ChatQuickAnswer.vue'
import Loading from '@/components/Shared/Loading.vue'
import { isAdminOrManager } from '@/service/user'

const props = {
  chat: {
    type: Object,
    required: false,
    validator: function(value) {
      if (typeof value !== 'object') return false
      if (!value.hasOwnProperty('messages')) return false
      if (!value.hasOwnProperty('meta')) return false
      return true
    },
  },
}

export default {
  props,
  components: { ChatWindow, ChatInputControl, ChatQuickAnswer, Loading },
  data() {
    return {
      loading: false,
      feeling: 'positive',
      messages: [],
    }
  },

  watch: {
    'chat.meta': function() {
      if (this.$refs.inputControl) this.$refs.inputControl.resetState()
    },
  },
  methods: {
    sendMessage({ message, isAnonymous }) {
      this.$emit('sendMessage', {
        feeling: this.feeling,
        message,
        isAnonymous,
      })

      this.reset()
    },

    emitRateMessage({ rating }) {
      this.$emit('rateMessage', { rating })
    },

    markAsNotRead() {
      this.loading = true
      this.$emit('markAsNotRead')
    },

    completeFeedback() {
      this.loading = true
      this.$emit('completeFeedback')
    },

    normalizeThread(messages) {
      const normalizeMessage = i =>
        Object.assign(i, {
          messages: [
            {
              id: `${i.message}${i.createdAt}`,
              time: i.createdAt,
              text: i.message.split(/\r\n|\r|\n/g),
              closed: i.closed || false,
              rating: i.rating || false,
            },
          ],
        })

      const translateAnonymous = name =>
        name === 'Anonymous' ? this.labels.anonymous : name

      const reduceThread = arr =>
        Object.values(
          arr.reduce((acc, cur) => {
            const dateIndex = this.$moment(cur.createdAt).format('YYYYMMDD')
            const last = arr => arr[arr.length - 1]
            const author = item => JSON.stringify((item || {}).author)
            const isSame = (item1, item2) => author(item1) === author(item2)

            if (!acc[dateIndex]) {
              acc[dateIndex] = {
                time: cur.createdAt,
                thread: [],
                authors: {},
              }
            }

            cur.author.firstname = translateAnonymous(cur.author.firstname)

            acc[dateIndex].authors[cur.author.firstname] = cur.author.firstname

            if (isSame(last(acc[dateIndex].thread), cur)) {
              last(acc[dateIndex].thread).messages.push(
                normalizeMessage(cur).messages[0]
              )
            } else {
              acc[dateIndex].thread.push(normalizeMessage(cur))
            }

            return acc
          }, [])
        )

      return reduceThread(messages)
    },

    reset() {
      this.loading = false
      if (this.$refs.inputControl) this.$refs.inputControl.resetState()
    },
  },
  computed: {
    ...mapGetters(['resources']),

    labels: function() {
      return this.resources.page.home.feedbacks.tickets
    },

    thread: function() {
      return this.normalizeThread(this.chat.messages)
    },

    showAnonymous: function() {
      return (
        this.isNewTicket ||
        (this.chat.meta.isAnonymous && this.chat.meta.isMyTicket)
      )
    },

    usersInChat: function() {
      const getAuthors = arr =>
        arr
          .reduce((acc, { authors }) => {
            return Array.from(new Set([...acc, ...Object.keys(authors)]))
          }, [])
          .join(', ')

      return this.thread.length ? getAuthors(this.thread) : ''
    },

    isNewTicket() {
      return !!this.chat.newTicket
    },

    canSeeOtherFeedbacks() {
      return isAdminOrManager()
    },
  },
}
</script>

<style lang="scss" scoped>
.popover-wrapper {
  margin-top: 0 !important;
}
</style>

<style lang="scss">
@import '~@/styles/custom/colors';
@import '~bootstrap/scss/_functions';
@import '~bootstrap/scss/_variables';
@import '~bootstrap/scss/mixins/breakpoints';

.chat-component {
  height: 100%;

  .chat-wrap {
    height: 100%;
  }

  .chat-box {
    display: flex;
    flex-direction: column;
    height: 100%;

    .status {
      align-items: center;
      box-sizing: content-box;
      display: flex;
      flex-wrap: nowrap;
      justify-content: flex-end;
      // min-height: 60px;
      padding: 0.7rem;

      @media (max-width: 425px) {
        justify-content: space-between;
      }

      .column-option {
        min-width: 250px;
      }

      .people-tags {
        align-items: center;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
        justify-content: center;
        text-align: right;

        @media (max-width: 425px) {
          align-items: center;
          flex-direction: column;
        }

        .people-in-thread {
          overflow: hidden;
          overflow-wrap: normal;
          text-align: left;
          text-overflow: ellipsis;
          width: 170px;
          white-space: nowrap;

          @media (max-width: 425px) {
            text-align: center;
          }
        }

        .tags {
          flex-grow: 1;
          flex-basis: 100%;

          @media (max-width: 425px) {
            order: 2;
            text-align: center;
          }
        }
      }

      .close-chat {
        align-items: center;
        color: map-get($theme-colors, 'primary');
        display: none;
        font-size: xx-large;
        height: 40px;
        justify-content: center;
        width: 24px;

        @media (max-width: 425px) {
          display: flex;
        }
      }

      .action-btn {
        cursor: pointer;
        font-size: 18pt;
        margin-top: 5px;
      }

      .complete-feedback-btn {
        border-radius: 10px !important;
        font-size: 0.9rem;
        display: flex;
        align-items: center;
        height: inherit !important;

        @include media-breakpoint-up(sm) {
          min-width: 125px;
        }
      }

      .action-wrapper {
        display: flex;
        align-items: center;
      }

      .loading {
        .lds-ellipsis {
          height: 17px;
          div {
            top: 5px;
          }
        }
      }
    }
  }
}
</style>
